import React from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import CTA from "../components/CTA";
import { solutionDetails } from "../data/solutions";
import {
  ArrowRight,
  ArrowLeft,
  Check,
  Plus,
  Minus,
  Sparkles,
} from "lucide-react";
import { useState } from "react";

export default function SolutionPage() {
  const { slug } = useParams();
  const solution = solutionDetails[slug];
  const [openFaq, setOpenFaq] = useState(0);

  if (!solution) return <Navigate to="/" replace />;

  const related = (solution.related || [])
    .map((s) => solutionDetails[s])
    .filter(Boolean);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <main className="pt-28">
        {/* Hero */}
        <section className="relative overflow-hidden">
          <div className="pointer-events-none absolute left-1/2 top-[60px] -z-10 h-[320px] w-[640px] -translate-x-1/2 rounded-full bg-[#d4af37]/12 blur-[120px]" />
          <div className="mx-auto max-w-6xl px-5 py-12 lg:px-8 lg:py-16">
            <Link
              to="/"
              className="inline-flex items-center gap-1.5 text-xs font-medium uppercase tracking-[0.14em] text-slate-500 hover:text-[#1e1b4b]"
            >
              <ArrowLeft className="h-3.5 w-3.5" /> All Solutions
            </Link>

            <div className="mt-6 inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-600">
              <span className="h-1.5 w-1.5 rounded-full bg-[#d4af37]" />
              {solution.eyebrow}
            </div>

            <h1 className="mt-5 text-5xl font-medium tracking-tight text-[#1e1b4b] sm:text-6xl">
              {solution.title}
            </h1>
            <p className="mt-5 max-w-3xl text-[19px] leading-relaxed text-[#1e1b4b]/80">
              {solution.tagline}
            </p>
            <p className="mt-5 max-w-3xl text-[15px] leading-relaxed text-slate-600">
              {solution.description}
            </p>

            <div className="mt-9 flex flex-wrap items-center gap-6">
              <div>
                <span className="text-4xl font-medium tracking-tight text-[#1e1b4b]">
                  {solution.metric}
                </span>
                <span className="ml-2 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                  {solution.metricLabel}
                </span>
              </div>
              <Link
                to="/demo"
                className="group inline-flex items-center gap-2 rounded-xl bg-[#1e1b4b] px-5 py-3 text-sm font-semibold text-white hover:bg-[#1e1b4b]/92"
              >
                Book a Demo
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
              </Link>
              <Link to="/pricing" className="text-sm font-medium text-[#1e1b4b] hover:text-[#d4af37]">
                See Pricing →
              </Link>
            </div>

            {/* Best for */}
            <div className="mt-10 flex flex-wrap items-center gap-3 border-t border-slate-200 pt-6">
              <span className="text-[10.5px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                Best for
              </span>
              {solution.bestFor.map((b) => (
                <span
                  key={b}
                  className="rounded-full bg-slate-100 px-3 py-1 text-xs text-slate-700"
                >
                  {b}
                </span>
              ))}
            </div>
          </div>
        </section>

        {/* Features */}
        <section className="border-t border-slate-200/80 bg-slate-50/40 py-20">
          <div className="mx-auto max-w-6xl px-5 lg:px-8">
            <div className="text-center">
              <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                Capabilities
              </p>
              <h2 className="mx-auto mt-3 max-w-2xl text-4xl font-medium tracking-tight text-[#1e1b4b]">
                What’s inside.
              </h2>
            </div>
            <div className="mt-12 grid grid-cols-1 gap-px overflow-hidden rounded-2xl border border-slate-200 bg-slate-200 sm:grid-cols-2 lg:grid-cols-3">
              {solution.features.map((f) => (
                <div
                  key={f.title}
                  className="flex flex-col bg-white p-6 transition-colors hover:bg-slate-50/70"
                >
                  <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-[#1e1b4b]/[0.06] text-[#1e1b4b]">
                    <Check className="h-4 w-4" />
                  </div>
                  <h3 className="mt-4 text-base font-semibold text-[#1e1b4b]">{f.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600">{f.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* How it works */}
        <section className="py-20">
          <div className="mx-auto max-w-6xl px-5 lg:px-8">
            <div className="text-center">
              <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                How It Works
              </p>
              <h2 className="mt-3 text-4xl font-medium tracking-tight text-[#1e1b4b]">
                Three steps to live.
              </h2>
            </div>
            <div className="mt-12 grid grid-cols-1 gap-5 md:grid-cols-3">
              {solution.howItWorks.map((s) => (
                <div
                  key={s.step}
                  className="rounded-2xl border border-slate-200 bg-white p-7 transition-all hover:-translate-y-1 hover:shadow-[0_24px_50px_-20px_rgba(30,27,75,0.18)]"
                >
                  <span className="font-mono text-xs font-semibold uppercase tracking-[0.18em] text-[#d4af37]">
                    {s.step}
                  </span>
                  <h3 className="mt-4 text-2xl font-semibold text-[#1e1b4b]">{s.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-slate-600">{s.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQs */}
        <section className="border-t border-slate-200/80 bg-white py-20">
          <div className="mx-auto max-w-3xl px-5 lg:px-8">
            <div className="text-center">
              <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                FAQ
              </p>
              <h2 className="mt-3 text-4xl font-medium tracking-tight text-[#1e1b4b]">
                Common questions.
              </h2>
            </div>
            <div className="mt-10 divide-y divide-slate-200 rounded-2xl border border-slate-200 bg-white">
              {solution.faqs.map((f, i) => {
                const isOpen = openFaq === i;
                return (
                  <button
                    key={i}
                    onClick={() => setOpenFaq(isOpen ? -1 : i)}
                    className="flex w-full flex-col px-5 py-5 text-left transition-colors hover:bg-slate-50/60"
                  >
                    <div className="flex items-center justify-between gap-4">
                      <span className="text-[15px] font-medium text-[#1e1b4b]">{f.q}</span>
                      <span
                        className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full border ${
                          isOpen ? "border-[#d4af37] bg-[#d4af37]/10 text-[#d4af37]" : "border-slate-200 text-slate-500"
                        }`}
                      >
                        {isOpen ? <Minus className="h-3.5 w-3.5" /> : <Plus className="h-3.5 w-3.5" />}
                      </span>
                    </div>
                    <div
                      className={`grid overflow-hidden transition-all duration-300 ${
                        isOpen ? "mt-3 grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"
                      }`}
                    >
                      <div className="min-h-0 pr-10 text-sm leading-relaxed text-slate-600">{f.a}</div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </section>

        {/* Related */}
        {related.length > 0 && (
          <section className="border-t border-slate-200/80 bg-slate-50/40 py-20">
            <div className="mx-auto max-w-6xl px-5 lg:px-8">
              <div className="flex items-end justify-between">
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                    Related
                  </p>
                  <h2 className="mt-2 text-3xl font-medium tracking-tight text-[#1e1b4b]">
                    Pairs well with.
                  </h2>
                </div>
                <Sparkles className="hidden h-5 w-5 text-[#d4af37] sm:block" />
              </div>
              <div className="mt-8 grid grid-cols-1 gap-5 md:grid-cols-3">
                {related.map((r) => (
                  <Link
                    key={r.slug}
                    to={`/solutions/${r.slug}`}
                    className="group rounded-2xl border border-slate-200 bg-white p-6 transition-all hover:-translate-y-1 hover:border-[#1e1b4b]/20 hover:shadow-md"
                  >
                    <p className="text-[10.5px] font-semibold uppercase tracking-wider text-slate-500">
                      {r.eyebrow}
                    </p>
                    <h3 className="mt-2 text-lg font-semibold text-[#1e1b4b]">{r.title}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-slate-600 line-clamp-2">
                      {r.tagline}
                    </p>
                    <span className="mt-4 inline-flex items-center gap-1 text-xs font-semibold text-[#1e1b4b] group-hover:text-[#d4af37]">
                      Learn more
                      <ArrowRight className="h-3 w-3 transition-transform group-hover:translate-x-0.5" />
                    </span>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}

        <CTA />
      </main>
      <Footer />
    </div>
  );
}
